import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Card,
  Button,
  Typography,
  Space,  
  Spin,
  Tag,
  Row,
  Col,
  Badge,
  message,
  Divider,
  Modal,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CrownOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  RobotOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { config } from "../config/env";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../services/api"; // Import your centralized api instance

const { Title, Text, Paragraph } = Typography;

// Initialize Stripe with your publishable key
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const stripePromise = loadStripe(config.stripe.publishableKey);

interface Plan {
  id: string;
  name: string;
  price: string;
  yearlyPrice: string;
  originalPrice?: string;
  yearlyOriginalPrice?: string;
  features: string[];
  stripePriceId?: string;
  stripeYearlyPriceId?: string;
  popular?: boolean;
  icon: React.ReactNode;
  color: string;
}

interface SubscriptionStatus {
  isSubscribed: boolean;
  planName: string | null;
  currentPeriodEnd: string | null;
  subscriptionId?: string;
  status?: "active" | "canceled" | "past_due";
}

const ManageSubscriptionPage: React.FC = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    "monthly",
  );
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Enhanced plans with better features and design
  const enhancedPlans: Plan[] = [
    {
      id: "basic",
      name: "Starter",
      price: "$29",
      yearlyPrice: "$299",
      originalPrice: "$49",
      yearlyOriginalPrice: "$499",
      features: [
        "Soil Health Monitoring",
        "Basic Energy Analytics",
        "Weather Integration",
        "Mobile Dashboard",
        "Email Alerts",
        "Data Export (CSV)",
        "Basic Reporting",
        "IoT Sensor Integration",
        "Real-time Data Visualization",
      ],
      stripePriceId: config.stripe.basicPriceId,
      stripeYearlyPriceId: config.stripe.basicYearlyPriceId,
      icon: <DatabaseOutlined />,
      color: "#1890ff",
    },
    {
      id: "premium",
      name: "Professional",
      price: "$79",
      yearlyPrice: "$799",
      originalPrice: "$97",
      yearlyOriginalPrice: "$999",
      features: [
        "Everything in Starter",
        "Advanced ML Predictions",
        "Graph Neural Network (GNN) Algorithms",
        "Knowledge Graph Access",
        "Real-time IoT Integration",
        "Custom Analytics",
        "Priority Support",
        "API Access",
        "Advanced Visualizations",
        "Predictive Modeling",
        "Anomaly Detection",
        "Optimization Recommendations",
        "Multi-location Management",
        "Advanced Security Features",
      ],
      stripePriceId: config.stripe.premiumPriceId,
      stripeYearlyPriceId: config.stripe.premiumYearlyPriceId,
      popular: true,
      icon: <CrownOutlined />,
      color: "#722ed1",
    },
  ];

  useEffect(() => {
    setPlans(enhancedPlans);
    fetchSubscriptionStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscriptionStatus = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await api.get('/api/subscriptions/status');
      if (response.data) {
        setSubscriptionStatus({
          isSubscribed: response.data.isSubscribed,
          planName: response.data.planName || null,
          currentPeriodEnd: response.data.currentPeriodEnd || null,
          subscriptionId: response.data.subscriptionId,
          status: response.data.status,
        });
      } else {
        // Fallback to mock data for development
        setSubscriptionStatus({
          isSubscribed: false,
          planName: null,
          currentPeriodEnd: null,
        });
      }
    } catch (err) {
      console.error("Error fetching subscription status:", err);
      // Fallback to mock data for development
      setSubscriptionStatus({
        isSubscribed: false,
        planName: null,
        currentPeriodEnd: null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (plan: Plan) => {
    const priceId =
      billingPeriod === "monthly"
        ? plan.stripePriceId
        : plan.stripeYearlyPriceId;

    if (!priceId) {
      message.error("Plan configuration error");
      return;
    }

    setSubscribing(plan.id);
    message.loading("Creating checkout session...", 0);

    try {
      // Call the new endpoint on the subscriptions controller
      const response = await api.post("/api/subscriptions/create-checkout-session", {
        priceId: priceId,
        successUrl: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/cancel`,
      });

      if (response.status !== 200 || !response.data.url) {
        throw new Error("Failed to create checkout session");
      }

      message.destroy();
      message.success(`Redirecting to checkout for ${plan.name}...`);

      // Redirect the user to the Stripe checkout URL provided by the backend
      window.location.href = response.data.url;
    } catch (err) {
      message.destroy();
      message.error(err instanceof Error ? err.message : "Subscription failed");
      console.error("Subscription error:", err);
    } finally {
      setSubscribing(null);
    }
  };

  const handleCancelSubscription = async () => {
    setShowCancelConfirm(false);
    try {
      setCancelling(true);
      message.loading("Cancelling subscription...", 0);

      const response = await api.post("/api/subscriptions/cancel");
      
      if (response.data.success) {
        message.success("Subscription cancelled successfully");
        await fetchSubscriptionStatus(); // Refresh status
      } else {
        message.error("Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      message.error("Failed to cancel subscription");
    } finally {
      setCancelling(false);
      message.destroy();
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          background: "#f0f2f5",
        }}
      >
        <Space direction="vertical" align="center">
          <Spin size="large" />
          <Text>Loading subscription information...</Text>
        </Space>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f0f2f5",
        padding: "24px",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "48px" }}>
          <Title level={1} style={{ marginBottom: "16px" }}>
            Expedient Subscription
          </Title>
          <Paragraph style={{ fontSize: "16px", color: "#666" }}>
            Unlock the full potential of intelligent agriculture and energy
            management
          </Paragraph>
        </div>

        {/* Current Subscription Status */}
        <Card
          style={{
            marginBottom: "32px",
            background: subscriptionStatus?.isSubscribed
              ? "#f6ffed"
              : "#fff7e6",
            border: subscriptionStatus?.isSubscribed
              ? "1px solid #b7eb8f"
              : "1px solid #ffd591",
          }}
        >
          <Row gutter={24} align="middle">
            <Col span={16}>
              <Space direction="vertical" size="small">
                <Title level={4} style={{ margin: 0 }}>
                  {subscriptionStatus?.isSubscribed ? (
                    <Space>
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      Active Subscription
                    </Space>
                  ) : (
                    <Space>
                      <CloseCircleOutlined style={{ color: "#faad14" }} />
                      No Active Subscription
                    </Space>
                  )}
                </Title>
                {subscriptionStatus?.isSubscribed ? (
                  <Space direction="vertical" size="small">
                    <Text>
                      Plan:{" "}
                      <Tag color="blue">{subscriptionStatus.planName}</Tag>
                    </Text>
                    <Text>Renews: {subscriptionStatus.currentPeriodEnd}</Text>
                    <Text>
                      Status: <Badge status="success" text="Active" />
                    </Text>
                  </Space>
                ) : (
                  <Text>
                    Choose a plan below to get started with Expedient
                  </Text>
                )}
              </Space>
            </Col>
            <Col span={8} style={{ textAlign: "right" }}>
              {subscriptionStatus?.isSubscribed &&
                (user?.roles?.includes("subscribed_user") || user?.roles?.includes("is_user_subscribed")) && (
                  <>
                    <Button
                      danger
                      loading={cancelling}
                      onClick={() => setShowCancelConfirm(true)}
                      icon={<CloseCircleOutlined />}
                    >
                      Cancel Subscription
                    </Button>
                    <Modal
                      title="Cancel Subscription"
                      open={showCancelConfirm}
                      onOk={handleCancelSubscription}
                      onCancel={() => setShowCancelConfirm(false)}
                      okText="Yes, Cancel"
                      okButtonProps={{ danger: true, loading: cancelling }}
                      cancelText="No, Keep Subscription"
                      confirmLoading={cancelling}
                    >
                      <p>Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.</p>
                    </Modal>
                  </>
                )}
            </Col>
          </Row>
        </Card>

        {/* Billing Period Toggle */}
        <Card style={{ marginBottom: "32px", textAlign: "center" }}>
          <Space direction="vertical" size="middle">
            <Title level={4} style={{ margin: 0 }}>
              Choose Your Billing Period
            </Title>
            <Space size="large">
              <Button
                type={billingPeriod === "monthly" ? "primary" : "default"}
                onClick={() => setBillingPeriod("monthly")}
                size="large"
              >
                Monthly Billing
              </Button>
              <Button
                type={billingPeriod === "yearly" ? "primary" : "default"}
                onClick={() => setBillingPeriod("yearly")}
                size="large"
              >
                Yearly Billing
                <Tag color="green" style={{ marginLeft: "8px" }}>
                  Save 30%
                </Tag>
              </Button>
            </Space>
          </Space>
        </Card>

        {/* Plans */}
        <Row gutter={[24, 24]} justify="center">
          {plans.map((plan) => (
            <Col xs={24} lg={12} key={plan.id}>
              <Card
                style={{
                  height: "100%",
                  position: "relative",
                  border: plan.popular
                    ? `2px solid ${plan.color}`
                    : "1px solid #d9d9d9",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
                styles={{ body: { padding: "32px" } }}
              >
                {plan.popular && (
                  <div
                    style={{
                      position: "absolute",
                      top: "0",
                      left: "0",
                      right: "0",
                      background: plan.color,
                      color: "white",
                      textAlign: "center",
                      padding: "8px",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    MOST POPULAR
                  </div>
                )}

                <div style={{ textAlign: "center", marginBottom: "32px" }}>
                  <div
                    style={{
                      fontSize: "48px",
                      color: plan.color,
                      marginBottom: "16px",
                    }}
                  >
                    {plan.icon}
                  </div>
                  <Title level={2} style={{ marginBottom: "8px" }}>
                    {plan.name}
                  </Title>
                  <Space align="baseline">
                    <Text
                      style={{
                        fontSize: "32px",
                        fontWeight: "bold",
                        color: plan.color,
                      }}
                    >
                      {billingPeriod === "monthly"
                        ? plan.price
                        : plan.yearlyPrice}
                    </Text>
                    <Text delete style={{ fontSize: "16px", color: "#999" }}>
                      {billingPeriod === "monthly"
                        ? plan.originalPrice
                        : plan.yearlyOriginalPrice}
                    </Text>
                    <Text>
                      /{billingPeriod === "monthly" ? "month" : "year"}
                    </Text>
                  </Space>
                  {billingPeriod === "yearly" && (
                    <div style={{ marginTop: "8px" }}>
                      <Tag color="green">Save 30%</Tag>
                    </div>
                  )}
                </div>

                <Divider />

                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: "100%" }}
                >
                  {plan.features.map((feature, index) => (
                    <div
                      key={index}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <CheckCircleOutlined
                        style={{ color: "#52c41a", marginRight: "8px" }}
                      />
                      <Text>{feature}</Text>
                    </div>
                  ))}
                </Space>

                <div style={{ marginTop: "32px", textAlign: "center" }}>
                  <Button
                    type="primary"
                    size="large"
                    loading={subscribing === plan.id}
                    onClick={() => handleSubscribe(plan)}
                    style={{
                      background: plan.color,
                      borderColor: plan.color,
                      height: "48px",
                      width: "100%",
                      borderRadius: "8px",
                    }}
                    icon={
                      plan.popular ? <CrownOutlined /> : <ThunderboltOutlined />
                    }
                  >
                    {subscribing === plan.id
                      ? "Processing..."
                      : `Get ${plan.name}`}
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Features Overview */}
        <Card style={{ marginTop: "48px" }}>
          <Title
            level={3}
            style={{ textAlign: "center", marginBottom: "32px" }}
          >
            Why Choose Expedient?
          </Title>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={8}>
              <div style={{ textAlign: "center" }}>
                <GlobalOutlined
                  style={{
                    fontSize: "48px",
                    color: "#1890ff",
                    marginBottom: "16px",
                  }}
                />
                <Title level={4}>Global Intelligence</Title>
                <Text>
                  Advanced AI and ML algorithms for predictive analytics
                </Text>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ textAlign: "center" }}>
                <SafetyCertificateOutlined
                  style={{
                    fontSize: "48px",
                    color: "#52c41a",
                    marginBottom: "16px",
                  }}
                />
                <Title level={4}>Enterprise Security</Title>
                <Text>Bank-level security with end-to-end encryption</Text>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ textAlign: "center" }}>
                <RobotOutlined
                  style={{
                    fontSize: "48px",
                    color: "#722ed1",
                    marginBottom: "16px",
                  }}
                />
                <Title level={4}>AI-Powered Insights</Title>
                <Text>Intelligent recommendations and automated workflows</Text>
              </div>
            </Col>
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default ManageSubscriptionPage;
